import { useState, useCallback, useRef } from "react";
import { uploadAsset } from "../../services/assetsApi";
import { config } from "../../config/environment";
import { AUDIO_MIME_TYPES, AUDIO_EXTENSIONS, getAcceptString, hasValidExtension } from "../../utils/fileTypes";
import {
  Box,
  Button,
  Icon,
  Spinner,
  Text,
  VStack,
  HStack,
} from "@chakra-ui/react";
import { MdUpload, MdDelete, MdMusicNote } from "react-icons/md";
import { usePlacedModelsStore } from "../../store";
import VideoErrorModal from "../Scene/VideoErrorModal";

const MAX_FILE_SIZE = config.upload.maxFileSize.mp3;
const MAX_FILE_SIZE_MB = Math.round(MAX_FILE_SIZE / (1024 * 1024));

export default function MusicUploadForObject({
  modelId,
  currentMusicUrl,
  onMusicChange,
  onMusicRemove,
}) {
  const roomId = usePlacedModelsStore((s) => s._roomId);
  const fileInputRef = useRef(null);

  const [uploading, setUploading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);

  const showError = useCallback((message) => {
    setErrorMessage(message);
    setShowErrorModal(true);
  }, []);

  const closeErrorModal = useCallback(() => {
    setShowErrorModal(false);
    setErrorMessage(null);
  }, []);

  const handleFileSelect = useCallback(
    async (event) => {
      const file = event.target.files?.[0];
      if (!file) return;

      // Validate file type — using canonical AUDIO_MIME_TYPES from fileTypes.js
      const fileName = file.name.toLowerCase();
      const hasValidMime = AUDIO_MIME_TYPES.includes(file.type);
      const extOk = hasValidExtension(file.name, 'audio');

      if (!hasValidMime && !extOk) {
        showError("Only MP3 and WAV files are supported");
        return;
      }

      // Validate file size
      if (file.size > MAX_FILE_SIZE) {
        showError(
          `File too large. Maximum size is ${MAX_FILE_SIZE_MB}MB.`
        );
        return;
      }

      setUploading(true);

      try {
        const result = await uploadAsset(file, { roomId });

        if (result.success) {
          const musicUrl = result.data.publicUrl || result.data.url;
          onMusicChange({ url: musicUrl, assetKey: result.data.assetKey });
        } else {
          showError(result.error?.message || "Upload failed");
        }
      } catch (err) {
        showError(err.message || "Upload failed");
      } finally {
        setUploading(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    },
    [roomId, showError, onMusicChange]
  );

  const handleDelete = useCallback(async () => {
    onMusicRemove();
  }, [onMusicRemove]);

  const triggerFileSelect = () => {
    fileInputRef.current?.click();
  };

  // Extract filename from URL for display
  const displayName = currentMusicUrl
    ? decodeURIComponent(currentMusicUrl.split("/").pop().split("?")[0])
    : null;

  return (
    <Box>
      <input
        ref={fileInputRef}
        type="file"
        accept={getAcceptString('audio')}
        onChange={handleFileSelect}
        style={{ display: "none" }}
      />

      {currentMusicUrl ? (
        <VStack spacing={3} align="stretch">
          {/* Current file display */}
          <HStack
            bg="gray.800"
            border="2px solid"
            borderColor="gray.600"
            borderRadius="0"
            p={3}
            spacing={3}
          >
            <Icon as={MdMusicNote} color="white" boxSize={5} />
            <Text
              fontSize="sm"
              color="gray.200"
              flex={1}
              isTruncated
              title={displayName}
            >
              {displayName}
            </Text>
          </HStack>

          {/* Actions */}
          <HStack spacing={2}>
            <Button
              size="sm"
              onClick={triggerFileSelect}
              isLoading={uploading}
              loadingText="Uploading..."
              flex={1}
              bg="gray.700"
              _hover={{ bg: "gray.600" }}
              borderRadius="0"
            >
              <Icon as={MdUpload} mr={1} />
              Replace
            </Button>
            <Button
              size="sm"
              onClick={handleDelete}
              variant="outline"
              flex={1}
              borderRadius="0"
              color="red.400"
              borderColor="red.400"
              _hover={{ bg: "red.900" }}
            >
              <Icon as={MdDelete} mr={1} />
              Remove
            </Button>
          </HStack>
        </VStack>
      ) : (
        <VStack spacing={3} align="stretch">
          {/* Upload button */}
          <Button
            onClick={triggerFileSelect}
            isLoading={uploading}
            loadingText="Uploading..."
            size="md"
            height="80px"
            bg="gray.800"
            border="2px dashed"
            borderColor="gray.600"
            _hover={{ bg: "gray.700", borderColor: "gray.500" }}
            borderRadius="0"
          >
            <VStack spacing={1}>
              {uploading ? (
                <Spinner size="md" />
              ) : (
                <>
                  <Icon as={MdMusicNote} boxSize={6} />
                  <Text fontSize="sm">Upload MP3</Text>
                </>
              )}
            </VStack>
          </Button>

          {/* Help text */}
          <Text fontSize="xs" color="gray.500" textAlign="center">
            MP3 up to {MAX_FILE_SIZE_MB}MB
          </Text>
        </VStack>
      )}

      {/* Error modal */}
      {showErrorModal && (
        <VideoErrorModal message={errorMessage} onClose={closeErrorModal} />
      )}
    </Box>
  );
}
